import {
  ActionIcon,
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Code,
  Container,
  Divider,
  Grid,
  Group,
  Modal,
  NumberFormatter,
  Pagination,
  Paper,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import {
  IconBook,
  IconBrandGoogle,
  IconBuildingBank,
  IconCalendar,
  IconCheck,
  IconClock,
  IconCoins,
  IconCopy,
  IconCreditCard,
  IconCurrencyDollar,
  IconEye,
  IconInfoCircle,
  IconMail,
  IconReceipt,
  IconRefresh,
  IconTag,
  IconTrendingUp,
  IconUser,
  IconUserCheck,
  IconUserX,
  IconX,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import React, { useState } from "react";

import { HttpSuccessCodesObj } from "../../Lib/data";
import { Endpoints } from "../../Lib/endpoints";
import {
  DefaultErrorNotification,
  getTransactionAmount,
} from "../../Lib/methods";
import { NonPaginatedResponse, PaginatedResponse } from "../../Lib/responses";
import {
  PaginationProps,
  PlanProps,
  StudentProfileProps,
  TransactionProps,
} from "../../Lib/types";
import { PerformRequest, usePerformRequest } from "../../Lib/usePerformRequest";
import "./styles.scss";
import { useForm } from "@mantine/form";

dayjs.extend(relativeTime);

const Transactions: React.FC = () => {
  const [pagination, setPagination] = useState<PaginationProps>({
    limit: 300,
    page: 1,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch] = useDebouncedValue(searchTerm, 300);
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [providerFilter, setProviderFilter] = useState<string | null>(null);
  const [selectedTransaction, setSelectedTransaction] =
    useState<TransactionProps | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<PlanProps | null>(null);
  const [selectedStudent, setSelectedStudent] =
    useState<StudentProfileProps | null>(null);
  const [isLoadingPlan, setIsLoadingPlan] = useState(false);
  const [isLoadingStudent, setIsLoadingStudent] = useState(false);
  const [activeTab, setActiveTab] = useState("all");

  const [
    isDetailModalOpened,
    { open: openDetailModal, close: closeDetailModal },
  ] = useDisclosure(false);

  // FILTER DATES ARE IN FORM OF DATE.NOW()
  const filterByDatesForm = useForm({
    initialValues: {
      from: dayjs().subtract(1, "month").valueOf(),
      to: Date.now(),
    },
    validate: {
      from: (value) => {
        if (dayjs(value).isAfter(dayjs(filterByDatesForm.values.to))) {
          return "From date must be before to date";
        }
      },
      to: (value) => {
        if (dayjs(value).isBefore(dayjs(filterByDatesForm.values.from))) {
          return "To date must be after from date";
        }
      },
    },
  });
  console.log(filterByDatesForm.values);
  const {
    data: transactions,
    isLoading: isLoadingTransactions,
    reloadData: reloadTransactions,
  } = usePerformRequest<PaginatedResponse<TransactionProps[]>>({
    method: Endpoints.GetStudentTransactions(pagination.page, pagination.limit)
      .method,
    route: Endpoints.GetStudentTransactions(pagination.page, pagination.limit)
      .route,
    body: {
      from: filterByDatesForm.values.from,
      to: filterByDatesForm.values.to,
    },
  });

  // Filter transactions based on search and filters
  const filteredTransactions =
    transactions?.data?.filter((transaction) => {
      // Only show new transactions, exclude renewals
      const isNewTransaction = transaction.type === "new";

      const matchesSearch =
        !debouncedSearch ||
        transaction.name
          .toLowerCase()
          .includes(debouncedSearch.toLowerCase()) ||
        transaction.reference
          .toLowerCase()
          .includes(debouncedSearch.toLowerCase()) ||
        transaction.studentID
          .toLowerCase()
          .includes(debouncedSearch.toLowerCase());

      const matchesType = !typeFilter || transaction.type === typeFilter;
      const matchesProvider =
        !providerFilter || transaction.provider === providerFilter;

      return (
        isNewTransaction && matchesSearch && matchesType && matchesProvider
      );
    }) || [];

  const handleTransactionClick = async (transaction: TransactionProps) => {
    setSelectedTransaction(transaction);
    setSelectedPlan(null);
    setSelectedStudent(null);
    openDetailModal();

    // Fetch plan details
    setIsLoadingPlan(true);
    try {
      const planResponse = await PerformRequest<
        NonPaginatedResponse<PlanProps>
      >({
        method: Endpoints.GetSinglePlanDetails(transaction.planID).method,
        route: Endpoints.GetSinglePlanDetails(transaction.planID).route,
        callback: () => {
          setIsLoadingPlan(false);
        },
      });

      if (planResponse && planResponse.status === HttpSuccessCodesObj.Success) {
        setSelectedPlan(planResponse?.data?.data);
      } else {
        DefaultErrorNotification("Failed to load plan details");
      }
    } catch (error) {
      DefaultErrorNotification("An error occurred while loading plan details");
    } finally {
      setIsLoadingPlan(false);
    }

    // Fetch student details
    setIsLoadingStudent(true);
    try {
      const studentResponse = await PerformRequest<
        NonPaginatedResponse<StudentProfileProps>
      >({
        method: Endpoints.GetSingleStudentDetails(transaction.studentID).method,
        route: Endpoints.GetSingleStudentDetails(transaction.studentID).route,
        callback: () => {
          setIsLoadingStudent(false);
        },
      });

      if (
        studentResponse &&
        studentResponse.status === HttpSuccessCodesObj.Success
      ) {
        setSelectedStudent(studentResponse?.data?.data);
      } else {
        DefaultErrorNotification("Failed to load student details");
      }
    } catch (error) {
      DefaultErrorNotification(
        "An error occurred while loading student details"
      );
    } finally {
      setIsLoadingStudent(false);
    }
  };

  const getTypeColor = (type: string) => {
    // Since we only show new transactions now
    return "green";
  };

  const getProviderColor = (provider: string) => {
    switch (provider) {
      case "paystack":
        return "blue";
      case "stripe":
        return "purple";
      default:
        return "gray";
    }
  };

  const getProviderIcon = (provider: string) => {
    switch (provider) {
      case "paystack":
        return <IconCreditCard size={16} />;
      case "stripe":
        return <IconBuildingBank size={16} />;
      case "offline":
        return <IconCoins size={16} />;
      default:
        return <IconReceipt size={16} />;
    }
  };

  const totalTransactions = transactions?.totalRecords || 0;

  const StatCard = ({ title, value, icon, color, description }: any) => (
    <Card shadow="sm" padding="xl" radius="md" withBorder>
      <Group justify="space-between">
        <div>
          <Text c="dimmed" size="sm" fw={500} tt="uppercase">
            {title}
          </Text>
          {isLoadingTransactions ? (
            <Skeleton height={24} width={100} my={4} />
          ) : (
            <Text fw={700} size="xl">
              {value}
            </Text>
          )}
          {description && (
            <Text c="dimmed" size="xs" mt={4}>
              {description}
            </Text>
          )}
        </div>
        <ThemeIcon color={color} size={60} radius="md">
          {icon}
        </ThemeIcon>
      </Group>
    </Card>
  );

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div style={{ padding: "32px", background: "#f8f9fa", minHeight: "100vh" }}>
      <Container size="xl">
        {/* Header */}
        <Group justify="space-between" align="center" mb={32}>
          <div>
            <Text size="xl" fw={700} mb={4}>
              Transaction Management
            </Text>
            <Text c="dimmed" size="sm">
              Monitor and manage all student transactions
            </Text>
          </div>
          <Button
            leftSection={<IconRefresh size={16} />}
            variant="filled"
            color="blue"
            onClick={reloadTransactions}
            loading={isLoadingTransactions}
          >
            Reload
          </Button>
        </Group>

        {/* Stats Cards */}
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="lg" mb={32}>
          <StatCard
            title="Total Transactions"
            value={
              <NumberFormatter value={totalTransactions} thousandSeparator />
            }
            icon={<IconReceipt size={30} />}
            color="blue"
            description="Total transactions"
          />
          {/* <StatCard
            title="Total Revenue"
            value={
              <NumberFormatter
                value={totalAmount}
                prefix="$"
                thousandSeparator
              />
            }
            icon={<IconTrendingUp size={30} />}
            color="green"
            description="From new purchases"
          /> */}
          {/* <StatCard
            title="Paystack Purchases"
            value={
              <NumberFormatter value={paystackTransactions} thousandSeparator />
            }
            icon={<IconCreditCard size={30} />}
            color="violet"
            description="Via Paystack gateway"
          />
          <StatCard
            title="Stripe Purchases"
            value={
              <NumberFormatter value={stripeTransactions} thousandSeparator />
            }
            icon={<IconBuildingBank size={30} />}
            color="orange"
            description="Via Stripe gateway"
          /> */}
        </SimpleGrid>

        {/* Transactions List */}
        {isLoadingTransactions ? (
          <SimpleGrid cols={{ base: 1, md: 2, lg: 3 }} spacing="lg">
            {Array.from({ length: 9 }).map((_, index) => (
              <Skeleton key={index} height={200} radius="md" />
            ))}
          </SimpleGrid>
        ) : filteredTransactions.length > 0 ? (
          <>
            <Grid gutter="lg">
              {filteredTransactions.map((transaction) => (
                <Grid.Col
                  key={transaction.id}
                  span={{ base: 12, md: 6, lg: 4 }}
                >
                  <Card
                    shadow="sm"
                    padding="lg"
                    radius="md"
                    withBorder
                    style={{
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      height: "100%",
                    }}
                    onClick={() => handleTransactionClick(transaction)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-4px)";
                      e.currentTarget.style.boxShadow =
                        "0 8px 25px rgba(0,0,0,0.12)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "";
                    }}
                  >
                    <Stack gap="md">
                      <Group justify="space-between" align="flex-start">
                        <Badge
                          color={getTypeColor(transaction.type)}
                          variant="light"
                          size="sm"
                          radius="xl"
                        >
                          {transaction.type === "new"
                            ? "New Purchase"
                            : "Renewal"}
                        </Badge>
                        <Badge
                          color={getProviderColor(transaction.provider)}
                          variant="filled"
                          size="sm"
                          radius="xl"
                          leftSection={getProviderIcon(transaction.provider)}
                        >
                          {transaction.provider.charAt(0).toUpperCase() +
                            transaction.provider.slice(1)}
                        </Badge>
                      </Group>

                      <div>
                        <Text fw={600} size="sm" lineClamp={1} mb={4}>
                          {transaction.name}
                        </Text>
                        <Text fw={700} size="lg" c="green">
                          {getTransactionAmount(transaction)}
                        </Text>
                        <Text c="dimmed" size="xs" lineClamp={1}>
                          Ref: {transaction.reference}
                        </Text>
                      </div>

                      <Divider />

                      <Group justify="space-between" align="center">
                        <Text size="xs" c="dimmed">
                          {dayjs(transaction.createdAt).format("MMM DD, YYYY")}
                        </Text>
                        <ActionIcon variant="subtle" size="sm" color="blue">
                          <IconEye size={14} />
                        </ActionIcon>
                      </Group>
                    </Stack>
                  </Card>
                </Grid.Col>
              ))}
            </Grid>

            {/* Pagination */}
            {totalTransactions > pagination.limit && (
              <Group justify="center" mt={32}>
                <Pagination
                  total={Math.ceil(totalTransactions / pagination.limit)}
                  value={pagination.page}
                  onChange={(page) => setPagination({ ...pagination, page })}
                  size="md"
                  radius="xl"
                  withEdges
                />
              </Group>
            )}
          </>
        ) : (
          <Paper
            style={{
              textAlign: "center",
              padding: "48px 24px",
              background: "#f8f9fa",
              border: "1px solid #e9ecef",
              borderRadius: 12,
            }}
          >
            <IconReceipt size={64} style={{ marginBottom: 16, opacity: 0.5 }} />
            <Text size="lg" fw={500} mb={8}>
              No transactions found
            </Text>
            <Text size="sm" c="dimmed">
              {searchTerm || typeFilter || providerFilter
                ? "Try adjusting your search criteria."
                : "There are no transactions in the system yet."}
            </Text>
          </Paper>
        )}

        {/* Transaction Detail Modal */}
        <Modal
          opened={isDetailModalOpened}
          onClose={closeDetailModal}
          title={
            <Group gap={12}>
              <ThemeIcon
                color={
                  selectedTransaction
                    ? getProviderColor(selectedTransaction.provider)
                    : "blue"
                }
                size={40}
                radius="xl"
              >
                {selectedTransaction ? (
                  getProviderIcon(selectedTransaction.provider)
                ) : (
                  <IconReceipt size={20} />
                )}
              </ThemeIcon>
              <div>
                <Text fw={600}>Transaction Details</Text>
                <Text size="sm" c="dimmed">
                  {selectedTransaction?.reference}
                </Text>
              </div>
            </Group>
          }
          size="lg"
          centered
          radius={12}
        >
          {selectedTransaction && (
            <Stack gap="lg">
              {/* Status and Amount */}
              <Card padding="lg" radius="md" style={{ background: "#f8f9fa" }}>
                <Group justify="space-between" align="center">
                  <div>
                    <Text size="sm" c="dimmed" mb={4}>
                      Transaction Amount
                    </Text>
                    <Text fw={700} size="xl" c="green">
                      <NumberFormatter
                        value={selectedTransaction.amount}
                        prefix="$"
                        thousandSeparator
                      />
                    </Text>
                  </div>
                  <Stack gap={8} align="flex-end">
                    <Badge
                      color={getTypeColor(selectedTransaction.type)}
                      variant="light"
                      size="lg"
                    >
                      {selectedTransaction.type === "new"
                        ? "New Purchase"
                        : "Renewal"}
                    </Badge>
                    <Badge
                      color={getProviderColor(selectedTransaction.provider)}
                      variant="filled"
                      size="md"
                      leftSection={getProviderIcon(
                        selectedTransaction.provider
                      )}
                    >
                      {selectedTransaction.provider.charAt(0).toUpperCase() +
                        selectedTransaction.provider.slice(1)}
                    </Badge>
                  </Stack>
                </Group>
              </Card>

              {/* Transaction Information */}
              <Stack gap="md">
                <Text fw={600} size="lg">
                  Transaction Information
                </Text>

                <Grid>
                  <Grid.Col span={6}>
                    <Stack gap={4}>
                      <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                        Transaction ID
                      </Text>
                      <Group gap={8}>
                        <Code>{selectedTransaction.id}</Code>
                        <Tooltip label="Copy ID">
                          <ActionIcon
                            size="xs"
                            variant="subtle"
                            onClick={() =>
                              copyToClipboard(selectedTransaction.id)
                            }
                          >
                            <IconCopy size={12} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Stack>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Stack gap={4}>
                      <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                        Reference
                      </Text>
                      <Group gap={8}>
                        <Code>{selectedTransaction.reference}</Code>
                        <Tooltip label="Copy Reference">
                          <ActionIcon
                            size="xs"
                            variant="subtle"
                            onClick={() =>
                              copyToClipboard(selectedTransaction.reference)
                            }
                          >
                            <IconCopy size={12} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Stack>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Stack gap={4}>
                      <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                        Student ID
                      </Text>
                      <Group gap={8}>
                        <Code>{selectedTransaction.studentID}</Code>
                        <Tooltip label="Copy Student ID">
                          <ActionIcon
                            size="xs"
                            variant="subtle"
                            onClick={() =>
                              copyToClipboard(selectedTransaction.studentID)
                            }
                          >
                            <IconCopy size={12} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Stack>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Stack gap={4}>
                      <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                        Plan ID
                      </Text>
                      <Group gap={8}>
                        <Code>{selectedTransaction.planID}</Code>
                        <Tooltip label="Copy Plan ID">
                          <ActionIcon
                            size="xs"
                            variant="subtle"
                            onClick={() =>
                              copyToClipboard(selectedTransaction.planID)
                            }
                          >
                            <IconCopy size={12} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Stack>
                  </Grid.Col>
                </Grid>

                <Divider />

                {/* Plan Information */}
                <Stack gap="md">
                  <Group gap={8} align="center">
                    <IconReceipt size={20} color="#228be6" />
                    <Text fw={700} size="lg" c="blue">
                      Plan Details
                    </Text>
                  </Group>

                  {isLoadingPlan ? (
                    <Card
                      padding="lg"
                      radius="lg"
                      withBorder
                      style={{
                        background:
                          "linear-gradient(135deg, #f8f9ff 0%, #e7f5ff 100%)",
                      }}
                    >
                      <Stack gap="md">
                        <Group justify="space-between">
                          <Skeleton height={20} width="50%" />
                          <Skeleton height={24} width={80} radius="xl" />
                        </Group>
                        <Skeleton height={16} width="80%" />
                        <Group gap="lg">
                          <div>
                            <Skeleton height={12} width={60} mb={4} />
                            <Skeleton height={18} width={80} />
                          </div>
                          <div>
                            <Skeleton height={12} width={40} mb={4} />
                            <Skeleton height={18} width={60} />
                          </div>
                        </Group>
                      </Stack>
                    </Card>
                  ) : selectedPlan ? (
                    <Card
                      padding="lg"
                      radius="lg"
                      withBorder
                      style={{
                        background:
                          "linear-gradient(135deg, #f8f9ff 0%, #e7f5ff 100%)",
                        border: "1px solid #339af0",
                      }}
                    >
                      <Stack gap="md">
                        <Group justify="space-between" align="flex-start">
                          <div style={{ flex: 1 }}>
                            <Group gap={8} mb={8}>
                              <ThemeIcon
                                size="sm"
                                color="blue"
                                variant="light"
                                radius="xl"
                              >
                                <IconReceipt size={14} />
                              </ThemeIcon>
                              <Text fw={600} size="lg" c="blue">
                                {selectedPlan.name}
                              </Text>
                            </Group>
                            <Text
                              size="sm"
                              c="dimmed"
                              lineClamp={3}
                              style={{ lineHeight: 1.5 }}
                            >
                              {selectedPlan.description ||
                                "No description available for this plan."}
                            </Text>
                          </div>
                          <Badge
                            color={selectedPlan.isActive ? "green" : "red"}
                            variant="filled"
                            size="lg"
                            radius="xl"
                            leftSection={
                              selectedPlan.isActive ? (
                                <IconCheck size={12} />
                              ) : (
                                <IconX size={12} />
                              )
                            }
                          >
                            {selectedPlan.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </Group>

                        <Divider color="#339af0" opacity={0.3} />

                        <Group justify="space-between">
                          <Card
                            padding="md"
                            radius="md"
                            style={{
                              background: "rgba(255,255,255,0.7)",
                              flex: 1,
                            }}
                          >
                            <Group gap={8} mb={4}>
                              <ThemeIcon
                                size="xs"
                                color="green"
                                variant="light"
                              >
                                <IconCurrencyDollar size={12} />
                              </ThemeIcon>
                              <Text
                                size="xs"
                                c="dimmed"
                                tt="uppercase"
                                fw={600}
                              >
                                Plan Price
                              </Text>
                            </Group>
                            <Text fw={700} size="xl" c="green">
                              <NumberFormatter
                                value={selectedPlan.price}
                                prefix="$"
                                thousandSeparator
                              />
                            </Text>
                          </Card>

                          <Card
                            padding="md"
                            radius="md"
                            style={{
                              background: "rgba(255,255,255,0.7)",
                              flex: 1,
                            }}
                          >
                            <Group gap={8} mb={4}>
                              <ThemeIcon
                                size="xs"
                                color="violet"
                                variant="light"
                              >
                                <IconTag size={12} />
                              </ThemeIcon>
                              <Text
                                size="xs"
                                c="dimmed"
                                tt="uppercase"
                                fw={600}
                              >
                                Category
                              </Text>
                            </Group>
                            <Badge
                              size="lg"
                              variant="gradient"
                              gradient={{ from: "violet", to: "blue" }}
                            >
                              {selectedPlan.tag || "General"}
                            </Badge>
                          </Card>
                        </Group>

                        <Group gap={4} justify="center" mt={4}>
                          <Text size="xs" c="dimmed">
                            Created{" "}
                            {dayjs(selectedPlan.createdAt).format(
                              "MMM DD, YYYY"
                            )}
                          </Text>
                          <Text size="xs" c="dimmed">
                            •
                          </Text>
                          <Text size="xs" c="dimmed">
                            ID: {selectedPlan.id.slice(0, 8)}...
                          </Text>
                        </Group>
                      </Stack>
                    </Card>
                  ) : (
                    <Card
                      padding="lg"
                      radius="lg"
                      withBorder
                      style={{
                        background: "#fff5f5",
                        border: "1px solid #ffa8a8",
                      }}
                    >
                      <Group gap={12} justify="center">
                        <ThemeIcon color="red" size="lg" variant="light">
                          <IconX size={20} />
                        </ThemeIcon>
                        <div>
                          <Text fw={600} c="red" size="md">
                            Plan Not Found
                          </Text>
                          <Text size="sm" c="dimmed">
                            Unable to load plan details for this transaction.
                          </Text>
                        </div>
                      </Group>
                    </Card>
                  )}
                </Stack>

                <Divider />

                {/* Student Information */}
                <Stack gap="md">
                  <Group gap={8} align="center">
                    <IconUser size={20} color="#20c997" />
                    <Text fw={700} size="lg" c="teal">
                      Student Details
                    </Text>
                  </Group>

                  {isLoadingStudent ? (
                    <Card
                      padding="lg"
                      radius="lg"
                      withBorder
                      style={{
                        background:
                          "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
                      }}
                    >
                      <Stack gap="md">
                        <Group gap="md">
                          <Skeleton height={50} width={50} radius="xl" />
                          <div style={{ flex: 1 }}>
                            <Skeleton height={18} width="60%" mb={6} />
                            <Skeleton height={14} width="80%" />
                          </div>
                          <Skeleton height={24} width={80} radius="xl" />
                        </Group>
                        <Group gap="lg" mt="md">
                          <Skeleton height={16} width="30%" />
                          <Skeleton height={16} width="40%" />
                          <Skeleton height={16} width="25%" />
                        </Group>
                      </Stack>
                    </Card>
                  ) : selectedStudent ? (
                    <Card
                      padding="lg"
                      radius="lg"
                      withBorder
                      style={{
                        background:
                          "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
                        border: "1px solid #10b981",
                      }}
                    >
                      <Stack gap="md">
                        <Group gap="md" align="flex-start">
                          <Avatar
                            size={50}
                            radius="xl"
                            style={{
                              border: "3px solid #10b981",
                              background:
                                "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                              color: "white",
                              fontWeight: 700,
                            }}
                          >
                            {selectedStudent.name
                              ?.split(" ")
                              .map((word) => word.charAt(0).toUpperCase())
                              .slice(0, 2)
                              .join("") || "??"}
                          </Avatar>

                          <div style={{ flex: 1 }}>
                            <Group gap={8} mb={4}>
                              <ThemeIcon
                                size="sm"
                                color="teal"
                                variant="light"
                                radius="xl"
                              >
                                <IconUser size={14} />
                              </ThemeIcon>
                              <Text fw={600} size="lg" c="teal">
                                {selectedStudent.name}
                              </Text>
                            </Group>
                            <Group gap={8} mb={6}>
                              <ThemeIcon size="xs" color="gray" variant="light">
                                <IconMail size={10} />
                              </ThemeIcon>
                              <Text size="sm" c="dimmed">
                                {selectedStudent.email}
                              </Text>
                            </Group>
                            <Group gap={8}>
                              <ThemeIcon size="xs" color="blue" variant="light">
                                <IconCalendar size={10} />
                              </ThemeIcon>
                              <Text size="xs" c="dimmed">
                                Member since{" "}
                                {dayjs(selectedStudent.createdAt).format(
                                  "MMM DD, YYYY"
                                )}
                              </Text>
                            </Group>
                          </div>

                          <Badge
                            color={
                              selectedStudent.isAccountSuspended
                                ? "red"
                                : selectedStudent.isActive
                                ? "green"
                                : "orange"
                            }
                            variant="filled"
                            size="lg"
                            radius="xl"
                            leftSection={
                              selectedStudent.isAccountSuspended ? (
                                <IconUserX size={12} />
                              ) : selectedStudent.isActive ? (
                                <IconUserCheck size={12} />
                              ) : (
                                <IconClock size={12} />
                              )
                            }
                          >
                            {selectedStudent.isAccountSuspended
                              ? "Suspended"
                              : selectedStudent.isActive
                              ? "Active"
                              : "Inactive"}
                          </Badge>
                        </Group>

                        <Divider color="#10b981" opacity={0.3} />

                        <SimpleGrid cols={3} spacing="xs">
                          <Card
                            padding="sm"
                            radius="md"
                            style={{
                              background: "rgba(255,255,255,0.7)",
                              textAlign: "center",
                            }}
                          >
                            <ThemeIcon
                              size="sm"
                              color={
                                selectedStudent.isEmailVerified
                                  ? "green"
                                  : "red"
                              }
                              variant="light"
                              mx="auto"
                              mb={4}
                            >
                              {selectedStudent.isEmailVerified ? (
                                <IconCheck size={14} />
                              ) : (
                                <IconX size={14} />
                              )}
                            </ThemeIcon>
                            <Text
                              size="xs"
                              fw={600}
                              c={
                                selectedStudent.isEmailVerified
                                  ? "green"
                                  : "red"
                              }
                            >
                              Email
                            </Text>
                            <Text size="xs" c="dimmed">
                              {selectedStudent.isEmailVerified
                                ? "Verified"
                                : "Unverified"}
                            </Text>
                          </Card>

                          <Card
                            padding="sm"
                            radius="md"
                            style={{
                              background: "rgba(255,255,255,0.7)",
                              textAlign: "center",
                            }}
                          >
                            <ThemeIcon
                              size="sm"
                              color={
                                selectedStudent.isPhoneVerified
                                  ? "green"
                                  : "red"
                              }
                              variant="light"
                              mx="auto"
                              mb={4}
                            >
                              {selectedStudent.isPhoneVerified ? (
                                <IconCheck size={14} />
                              ) : (
                                <IconX size={14} />
                              )}
                            </ThemeIcon>
                            <Text
                              size="xs"
                              fw={600}
                              c={
                                selectedStudent.isPhoneVerified
                                  ? "green"
                                  : "red"
                              }
                            >
                              Phone
                            </Text>
                            <Text size="xs" c="dimmed">
                              {selectedStudent.isPhoneVerified
                                ? "Verified"
                                : "Unverified"}
                            </Text>
                          </Card>

                          <Card
                            padding="sm"
                            radius="md"
                            style={{
                              background: "rgba(255,255,255,0.7)",
                              textAlign: "center",
                            }}
                          >
                            <ThemeIcon
                              size="sm"
                              color="blue"
                              variant="light"
                              mx="auto"
                              mb={4}
                            >
                              <IconBook size={14} />
                            </ThemeIcon>
                            <Text size="xs" fw={600} c="blue">
                              Plans
                            </Text>
                            <Text size="xs" c="dimmed">
                              {selectedStudent.enrolledPlans?.length || 0}{" "}
                              enrolled
                            </Text>
                          </Card>
                        </SimpleGrid>

                        <Card
                          padding="sm"
                          radius="md"
                          style={{ background: "rgba(255,255,255,0.5)" }}
                        >
                          <Group gap={8} justify="center">
                            <ThemeIcon size="xs" color="gray" variant="light">
                              <IconBrandGoogle size={10} />
                            </ThemeIcon>
                            <Text size="xs" c="dimmed">
                              Login method:
                            </Text>
                            <Code
                              style={{ background: "rgba(255,255,255,0.8)" }}
                            >
                              {selectedStudent.login_method}
                            </Code>
                          </Group>
                        </Card>
                      </Stack>
                    </Card>
                  ) : (
                    <Card
                      padding="lg"
                      radius="lg"
                      withBorder
                      style={{
                        background: "#fff5f5",
                        border: "1px solid #ffa8a8",
                      }}
                    >
                      <Group gap={12} justify="center">
                        <ThemeIcon color="red" size="lg" variant="light">
                          <IconX size={20} />
                        </ThemeIcon>
                        <div>
                          <Text fw={600} c="red" size="md">
                            Student Not Found
                          </Text>
                          <Text size="sm" c="dimmed">
                            Unable to load student details for this transaction.
                          </Text>
                        </div>
                      </Group>
                    </Card>
                  )}
                </Stack>

                <Divider />

                {/* Timing Information */}
                <Stack gap="md">
                  <Text fw={600} size="md">
                    Timing Information
                  </Text>
                  <Group justify="space-between">
                    <Group gap={8}>
                      <IconCalendar size={16} color="#868e96" />
                      <Text size="sm" c="dimmed">
                        Transaction Date
                      </Text>
                    </Group>
                    <Text size="sm" fw={500}>
                      {dayjs(selectedTransaction.createdAt).format(
                        "MMMM DD, YYYY"
                      )}
                    </Text>
                  </Group>
                  <Group justify="space-between">
                    <Group gap={8}>
                      <IconClock size={16} color="#868e96" />
                      <Text size="sm" c="dimmed">
                        Time
                      </Text>
                    </Group>
                    <Text size="sm" fw={500}>
                      {dayjs(selectedTransaction.createdAt).format("HH:mm:ss")}
                    </Text>
                  </Group>
                  <Group justify="space-between">
                    <Group gap={8}>
                      <IconInfoCircle size={16} color="#868e96" />
                      <Text size="sm" c="dimmed">
                        Relative Time
                      </Text>
                    </Group>
                    <Text size="sm" fw={500}>
                      {dayjs(selectedTransaction.createdAt).fromNow()}
                    </Text>
                  </Group>
                </Stack>

                <Divider />

                {/* Transaction Details Summary */}
                <Alert
                  icon={<IconInfoCircle size={16} />}
                  title="Transaction Summary"
                  color="blue"
                  variant="light"
                >
                  <Text size="sm">
                    <Text component="span" fw={500}>
                      {selectedTransaction.name}
                    </Text>{" "}
                    made a{" "}
                    <Text
                      component="span"
                      fw={500}
                      c={getTypeColor(selectedTransaction.type)}
                    >
                      {selectedTransaction.type}
                    </Text>{" "}
                    payment of{" "}
                    <Text component="span" fw={700} c="green">
                      {getTransactionAmount(selectedTransaction)}
                    </Text>{" "}
                    via{" "}
                    <Text component="span" fw={500}>
                      {selectedTransaction.provider}
                    </Text>{" "}
                    on{" "}
                    <Text component="span" fw={500}>
                      {dayjs(selectedTransaction.createdAt).format(
                        "MMMM DD, YYYY"
                      )}
                    </Text>
                    .
                  </Text>
                </Alert>
              </Stack>
            </Stack>
          )}
        </Modal>
      </Container>
    </div>
  );
};

export default Transactions;
